// Light / dark switch for the public site.
//   variant="icon": round rail button; the sun and moon swap with a spin.
//   variant="pill": labelled track with a sliding knob (menu panel, footer).
// Clicking grows the new theme out of the switch as a circle (siteTheme.ts).
import { AnimatePresence, motion } from "framer-motion";
import { Moon, Sun } from "lucide-react";
import { setSiteTheme, useSiteTheme } from "./siteTheme";

const ease = [0.76, 0, 0.24, 1] as const;

export function ThemeSwitch({ variant = "icon", className = "" }: { variant?: "icon" | "pill"; className?: string }) {
  const theme = useSiteTheme();
  const dark = theme === "dark";
  const toggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setSiteTheme(dark ? "light" : "dark", { x: r.left + r.width / 2, y: r.top + r.height / 2 });
  };
  const label = dark ? "Switch to light mode" : "Switch to dark mode";

  if (variant === "icon") {
    return (
      <button
        type="button"
        role="switch"
        aria-checked={dark}
        aria-label={label}
        title={label}
        onClick={toggle}
        className={`relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-cx-fg/15 text-cx-fg/80 transition-colors hover:border-cx-accent hover:text-cx-fg ${className}`}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={theme}
            initial={{ y: 22, rotate: -90, opacity: 0 }}
            animate={{ y: 0, rotate: 0, opacity: 1 }}
            exit={{ y: -22, rotate: 90, opacity: 0 }}
            transition={{ duration: 0.45, ease }}
            className="flex"
          >
            {dark ? <Moon className="h-[18px] w-[18px]" strokeWidth={1.6} /> : <Sun className="h-[18px] w-[18px]" strokeWidth={1.6} />}
          </motion.span>
        </AnimatePresence>
      </button>
    );
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      aria-label={label}
      onClick={toggle}
      className={`relative inline-grid grid-cols-2 items-center rounded-full border border-cx-fg/15 p-1 font-hud text-[10px] uppercase tracking-[0.2em] ${className}`}
    >
      <motion.span
        aria-hidden
        className="absolute inset-y-1 left-1 w-[calc(50%-4px)] rounded-full bg-[#2A629E]"
        animate={{ x: dark ? "100%" : "0%" }}
        transition={{ type: "spring", stiffness: 420, damping: 34 }}
      />
      <span className={`relative z-10 flex items-center justify-center gap-1.5 px-4 py-2 transition-colors ${dark ? "text-cx-muted" : "text-white"}`}>
        <Sun className="h-3.5 w-3.5" strokeWidth={1.8} /> Light
      </span>
      <span className={`relative z-10 flex items-center justify-center gap-1.5 px-4 py-2 transition-colors ${dark ? "text-white" : "text-cx-muted"}`}>
        <Moon className="h-3.5 w-3.5" strokeWidth={1.8} /> Dark
      </span>
    </button>
  );
}
